import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ReactNode, useState } from "react";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";

interface ConfirmRegisterButtonProps {
  onConfirm: () => void;
  isLoading?: boolean;
  children?: ReactNode;
}

export function ConfirmRegisterButton({
  onConfirm,
  isLoading = false,
  children,
}: ConfirmRegisterButtonProps) {
  const [isChecked, setIsChecked] = useState(false);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          className="font-bold bg-[#F5CB2B] 
                hover:from-[#F5CB2B] hover:to-[#F5A524] 
                text-gray-700 rounded-xl px-6 py-3 text-base
                shadow-lg hover:shadow-xl transition-all duration-200 
                hover:scale-105"
          disabled={isLoading}
        >
          {children || "Confirmar"}
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent className="w-full max-w-2xl bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 shadow-2xl rounded-2xl p-0 overflow-hidden">
        <div className="bg-[#F5CB2B] p-6">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <AlertDialogTitle className="text-2xl font-bold text-white">
                Confirmar Criação de Exceção
              </AlertDialogTitle>
            </div>
          </AlertDialogHeader>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-red-800">Ação Irreversível</p>
                <p className="text-sm text-red-700">
                  Após criada, não será possível editar ou excluir a Regra de
                  Exceção diretamente. Revise cuidadosamente todos os dados
                  antes de confirmar.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-blue-800">Dica de Revisão</p>
                <p className="text-sm text-blue-700">
                  Utilize{" "}
                  <kbd className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-mono font-semibold">
                    Ctrl + F
                  </kbd>{" "}
                  para localizar e corrigir informações antes de confirmar.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-yellow-800">
                  Vigência da Alíquota
                </p>
                <p className="text-sm text-yellow-700">
                  A nova alíquota só entrará em vigor após o encerramento da
                  anterior, caso exista. Mesmo que a data de início esteja
                  definida para hoje, ela será considerada apenas a partir do
                  próximo ciclo de atualização do sistema, que ocorre
                  diariamente.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-xl p-4 mt-6">
            <div className="flex items-center gap-3">
              <Checkbox
                id="acknowledge"
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(Boolean(checked))}
                className="data-[state=checked]:bg-[#F5A524] data-[state=checked]:border-[#F5A524] w-5 h-5"
              />
              <Label
                htmlFor="acknowledge"
                className="text-sm text-gray-800 font-medium cursor-pointer select-none"
              >
                Li e estou ciente de todas as informações acima. Desejo
                prosseguir com o cadastro.
              </Label>
              {isChecked && (
                <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
              )}
            </div>
          </div>
        </div>

        <AlertDialogFooter className="bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          <AlertDialogCancel
            className="border-2 border-gray-300 rounded-xl text-gray-700 bg-white 
                hover:bg-gray-50 hover:border-red-400 hover:text-red-500 hover:shadow-xl transition-all duration-200 
                hover:scale-105
                px-6 py-3  font-medium shadow-sm"
          >
            Cancelar
          </AlertDialogCancel>
          <Button
            className="bg-[#F5CB2B] 
                hover:from-[#F5CB2B] hover:to-[#F5A524] 
                text-gray-700 rounded-xl px-6 py-3 text-base font-medium 
                shadow-lg hover:shadow-xl transition-all duration-200 
                hover:scale-105"
            onClick={onConfirm}
            disabled={!isChecked || isLoading}
            isLoading={isLoading}
          >
            {isLoading ? "Criando..." : "Criar Exceção"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
